/**
 * Lectura del audit log y derivación del estado de cada confirmación.
 *
 * El front lee esta colección DIRECTO con el SDK de cliente (CLAUDE.md "Colisiones resueltas"): las
 * `firestore.rules` dan `allow read` a la lista nombrada y `allow write: if false`, así que no hay
 * endpoint de lectura que esperar y el invariante 6 lo sigue sosteniendo la capa de acceso del
 * backend, no estas reglas.
 *
 * ⚠️ NOMBRES DE CAMPO: el `CLAUDE.md` documenta el esquema en snake_case (`preview_id`,
 * `created_at`, `n_viajes_enviados`) pero `functions/src/audit/auditLog.ts` escribe el objeto con
 * `...entry`, así que lo que hay en Firestore es camelCase (`previewId`, `createdAt`,
 * `nViajesEnviados`). Este módulo codifica contra el CÓDIGO, que es lo que existe. Si algún día se
 * alinean, es aquí donde se toca.
 *
 * Este módulo es PURO a propósito: no importa el SDK de cliente ni `./firebase`, así que la
 * derivación de estados se puede ejecutar en cualquier sitio — tests en node, y el harness que
 * comprueba contra un Firestore real que lo que el backend escribe es lo que esto lee. La lectura
 * vive aparte, en `auditLogFirestore.ts`. Con `db` importado aquí arriba, nada de eso era posible.
 */
import { EstadoColaSchema, type Criterios, type Resumen } from '@caes-payments/shared';
import { describirPatron } from './seleccion';

export type BackendMode = 'mock' | 'real';

/**
 * Lo que el backend escribe hoy. `progress` está pedido en BB-1617 y todavía NO lo escribe nadie:
 * está aquí porque la derivación de estados tiene que funcionar el día que aparezca sin tocar la UI,
 * y porque sin él "en curso" solo se puede inferir por antigüedad (ver `derivarEstado`).
 */
export type TipoEvento = 'intent' | 'progress' | 'outcome' | 'cancelacion';

export interface EventoAudit {
  id: string;
  tipo: TipoEvento;
  previewId: string;
  /** ms epoch. `null` si el serverTimestamp aún no ha resuelto (escritura recién hecha). */
  createdAtMs: number | null;
  actor: { uid: string; email: string };
  criterios: Criterios | null;
  resumen: Resumen | null;
  snapshotTable: string | null;
  backendMode: BackendMode;
  /** Solo en `outcome`: tras el dedupe. */
  nViajesEnviados: number | null;
  /** Solo en `outcome`. `status` puede ser 'ok' | 'error' | 'partial' (CLAUDE.md). */
  backendResult: { status: string; error?: string; fallidos?: unknown[] } | null;
  /** Solo en `progress`: acumulados. */
  progreso: { nEnviados: number; nOk: number; nError: number } | null;
  /** Solo en `cancelacion` (BB-1676): lo que quedaba sin procesar y lo que ya se había enviado. */
  cancelacion: { tandasPendientes: number; nViajesYaEnviados: number } | null;
  /**
   * Solo en `intent`, y solo si hay más de una tanda o la única empieza en el futuro (BB-1667 Fase 2):
   * cuándo puede empezar cada tanda según el ledger de cuota. `null` = sin plan (todo salió ya).
   */
  plan: { desdeMs: number; nViajes: number }[] | null;
}

export type EstadoConfirmacion =
  | 'completado'
  | 'parcial'
  | 'fallido'
  | 'en-curso'
  /**
   * Encolada y todavía sin empezar: ningún chunk ha terminado, así que **el dinero no se ha movido**.
   *
   * Existe porque antes esto se pintaba como `sin-resolver` —rojo, "hay que reconciliar a mano"— en
   * cuanto pasaban 5 minutos. Y desde BB-1667 es un estado normal y frecuente: un chunk que se topa
   * con la cuota diaria espera y se reintenta, así que una confirmación puede estar legítimamente en
   * cola durante horas. Confundir "esperando" con "pago roto" arruina justo la señal que tiene que
   * ser fiable.
   */
  | 'en-cola'
  /**
   * Alguien paró lo que quedaba por pagar (BB-1676).
   *
   * **No significa que no se moviera dinero**: lo ya enviado está enviado, y la fila lo dice con la
   * cifra delante. Significa que las tandas pendientes no se van a ejecutar.
   *
   * Sale del evento de cancelación, no del `outcome`, porque los dos momentos no coinciden: una tanda
   * diferida a mañana no se despacha hasta mañana, así que el `outcome` puede tardar horas. Sin este
   * estado, entre medias la fila diría "en cola" — que es exactamente lo contrario de lo que pasa.
   */
  | 'cancelada'
  /**
   * BB-2625. Tiene tandas pendientes, la cola AVANZA (el latido de `estado_cola` es reciente) y esta
   * confirmación lleva un rato sin tanda propia: la cola está sirviendo a otra. Gris: es esperar.
   *
   * Existe por el 2026-10-01: con dos confirmaciones en vuelo, la cola (una tanda a la vez) sirvió casi
   * solo a la grande, y la de Extremadura se pintó "Sin resultado registrado — hay que reconciliarlo a
   * mano" mientras se pagaba una tanda cada 18 s sin un error.
   */
  | 'esperando-turno'
  /**
   * BB-2625. Ninguna tanda de NINGUNA confirmación ha terminado en `MARGEN_COLA_MS`: la cola está
   * pausada, o todas sus tandas fallan y se reintentan. Rojo: es la alarma que "sin resolver" quería
   * ser y no podía, porque solo veía el silencio de cada confirmación.
   */
  | 'cola-parada'
  | 'sin-resolver';

export interface Confirmacion {
  previewId: string;
  estado: EstadoConfirmacion;
  /**
   * `true` cuando el estado NO sale de un hecho registrado sino de comparar antigüedad con el
   * timeout de la función. Pasa mientras no existan eventos `progress` (BB-1617). La UI lo dice en
   * vez de afirmar el estado a secas: un "en curso" inferido y uno con latido real no valen lo mismo.
   */
  inferido: boolean;
  /**
   * Cuántas veces se intentó confirmar este mismo preview. Normalmente 1. Más de 1 significa que
   * alguien reintentó —típicamente tras un timeout— y la pantalla lo tiene que decir: el endpoint es
   * idempotente, así que no se paga doble, pero "se confirmó dos veces" es exactamente el tipo de
   * hecho que un audit log no puede esconder.
   */
  nIntentos: number;
  iniciadaMs: number | null;
  ultimaSenalMs: number | null;
  actor: { uid: string; email: string };
  criterios: Criterios | null;
  resumen: Resumen | null;
  snapshotTable: string | null;
  backendMode: BackendMode;
  nViajesEnviados: number | null;
  /** Último acumulado conocido. Cuando un job muere, esto es hasta dónde llegó. */
  progreso: { nEnviados: number; nOk: number; nError: number } | null;
  /**
   * Los `progress` de esta confirmación, para que el ETA mida el ritmo RECIENTE y no el promedio
   * desde el arranque (ver `eta.ts`). Solo cuándo y cuánto llevaba enviado: es lo único que hace
   * falta para un ritmo, y así la lista no crece con datos que nadie usa.
   *
   * Topada a las más recientes porque una confirmación grande escribe miles de `progress` y esto se
   * recalcula en cada polling; con la ventana de 10 minutos, unas pocas decenas sobran.
   */
  muestrasProgreso: { ms: number; nEnviados: number }[];
  error: string | null;
  /**
   * `resumen.nViajes` != `nViajesEnviados` significa que el dedupe quitó filas, o sea fanout —
   * justo lo que vigila el invariante 1. Se calcula aquí para que la UI no tenga que restar.
   */
  huboDedupe: boolean;
  /**
   * Cuándo empieza la siguiente tanda que el plan de cuota dejó para más tarde — SOLO si las tandas que
   * ya tocaban están enviadas enteras. Mientras exista, que la confirmación (o la cola entera) esté
   * callada es lo previsto. `null` también cuando hay tanda futura pero se va por detrás del plan.
   */
  proximaTandaMs: number | null;
  /** BB-1676: la cancelación, si la hubo. `null` es el caso normal. */
  cancelada: {
    ms: number | null;
    /** Quién la pidió. Es siempre quien confirmó — lo comprueba el backend. */
    email: string;
    tandasPendientes: number;
    /** Viajes ya enviados al endpoint cuando se canceló: dinero que YA se movió. */
    nViajesYaEnviados: number;
  } | null;
}

/**
 * ¿Puede esta persona cancelar esta confirmación? (BB-1676)
 *
 * Dos condiciones, y las dos son de UX — la de verdad la aplica el backend contra `confirm_jobs.actor`:
 *
 * 1. **Es quien confirmó.** «solo el que confirmó puede cancelar».
 * 2. **Queda algo que parar.** Un estado terminal ya no tiene tandas pendientes, y ofrecer una X que
 *    el backend va a rechazar es peor que no ofrecerla.
 *
 * `sin-resolver` SÍ entra: un job que dejó de dar señales puede tener tareas en reintento hasta 3 días,
 * así que pararlo significa algo. Es justo el caso en que alguien quiere el botón.
 */
export function puedeCancelar(c: Confirmacion, uid: string | null): boolean {
  if (uid === null || c.actor.uid !== uid) return false;
  return (
    c.estado === 'en-cola' ||
    c.estado === 'en-curso' ||
    c.estado === 'esperando-turno' ||
    c.estado === 'cola-parada' ||
    c.estado === 'sin-resolver'
  );
}

/**
 * Margen sobre el timeout de la función (60 s hoy, ver BB-1617) para decidir que un `intent` sin
 * `outcome` ya no puede estar corriendo. Generoso a propósito: el reloj del navegador y el
 * `serverTimestamp` de Firestore no son el mismo reloj, y un desfase de cliente no debe convertir un
 * job vivo en "sin resolver" — equivocarse hacia "en curso" solo retrasa una alarma, mientras que
 * equivocarse hacia "sin resolver" manda a alguien a reconciliar un pago que iba bien.
 */
export const MARGEN_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Estado de UNA confirmación a partir de todos sus eventos. Función pura y con `ahoraMs` inyectado
 * para que sea testeable sin congelar el reloj.
 *
 * Los cuatro estados que hacen falta, y por qué no valen dos: con `intent`/`outcome` a secas, un job
 * corriendo y un job muerto son el MISMO dato (un intent sin outcome). Con confirmaciones que a 40
 * req/s duran minutos u horas (BB-1617), "en curso" es el estado normal, no un parpadeo.
 */
/**
 * Tope de muestras de progreso que se conservan por confirmación. La ventana del ETA son 10 minutos
 * (~65 chunks al ritmo de una confirmación grande), así que 120 la cubren de sobra incluso si el
 * ritmo se duplica, y evitan arrastrar miles de latidos en cada recálculo.
 */
const MAX_MUESTRAS_PROGRESO = 120;

export function derivarEstado(eventos: readonly EventoAudit[], ahoraMs: number): Confirmacion | null {
  const primero = eventos[0];
  if (primero === undefined) return null;

  const porTiempo = (a: EventoAudit, b: EventoAudit) => (a.createdAtMs ?? 0) - (b.createdAtMs ?? 0);

  // Un mismo previewId puede tener VARIOS intentos: si /confirm muere por timeout (BB-1617) Ops
  // reintenta, y cada reintento escribe su propio intent y su propio outcome. Encontrado con el
  // harness contra el emulador, no razonando: confirmar dos veces dejaba 4 eventos y esta función
  // pintaba una sola confirmación como si nada.
  const intents = eventos.filter((e) => e.tipo === 'intent').sort(porTiempo);
  const outcomes = eventos.filter((e) => e.tipo === 'outcome').sort(porTiempo);

  // El PRIMER intent dice cuándo empezó todo; el ÚLTIMO outcome es el resultado que vale. Coger
  // `find()` sobre el array sin ordenar hacía que, con un ok y un error, el estado pintado
  // dependiera del orden en que Firestore devolviera los documentos.
  const intent = intents[0] ?? null;
  const outcome = outcomes.length > 0 ? (outcomes[outcomes.length - 1] ?? null) : null;

  const progresos = eventos
    .filter((e) => e.tipo === 'progress' && e.progreso !== null)
    .sort(porTiempo);
  const ultimoProgreso = progresos.length > 0 ? progresos[progresos.length - 1] : null;

  // Cualquier evento sirve de base: todos llevan actor, criterios, resumen y backendMode. Se
  // prefiere el intent porque es el que Ops generó, y el outcome como respaldo si el intent
  // se hubiera perdido (no debería: se escribe antes de llamar a Backend).
  const base = intent ?? outcome ?? primero;

  const iniciadaMs = intent?.createdAtMs ?? base.createdAtMs;
  const ultimaSenalMs = maximo([
    iniciadaMs,
    ultimoProgreso?.createdAtMs ?? null,
    outcome?.createdAtMs ?? null,
  ]);

  const progreso = ultimoProgreso?.progreso ?? null;
  const nViajesEnviados = outcome?.nViajesEnviados ?? null;

  // Ya vienen ordenados por tiempo; se descartan los que no tienen fecha, porque sin `ms` no miden
  // nada, y se queda solo la cola: es donde está la ventana reciente.
  const muestrasProgreso = progresos
    .flatMap((e) =>
      e.createdAtMs === null || e.progreso === null
        ? []
        : [{ ms: e.createdAtMs, nEnviados: e.progreso.nEnviados }],
    )
    .slice(-MAX_MUESTRAS_PROGRESO);

  // BB-1676. El PRIMERO si hubiera más de uno: el backend no escribe dos (el segundo clic responde
  // `ya-estaba-cancelada` sin registrar nada), pero el audit log es insert-only y esta pantalla no
  // puede depender de que eso siga siendo así — la fecha que interesa es cuándo se paró.
  const cancelaciones = eventos
    .filter((e) => e.tipo === 'cancelacion' && e.cancelacion !== null)
    .sort(porTiempo);
  const cancelacion = cancelaciones[0] ?? null;

  // El plan lo escribe el `intent`; se miran todos por si hubo reintentos de /confirm.
  //
  // Una tanda futura solo explica el silencio si las tandas que YA podían empezar están enviadas
  // enteras. Si no, lo que falta de ahora no espera cuota: está fallando o parado, y se clasifica como
  // siempre. Hallazgo del payment-safety-reviewer: sin esta condición, una confirmación partida en
  // "300.000 ya + 100.000 mañana" cuya primera tanda falla se pintaba "En cola" en gris hasta mañana.
  const tandasPlan = intents.flatMap((e) => e.plan ?? []);
  const viajesQueYaTocaban = tandasPlan
    .filter((t) => t.desdeMs <= ahoraMs)
    .reduce((n, t) => n + t.nViajes, 0);
  const planAlDia = (ultimoProgreso?.progreso?.nEnviados ?? 0) >= viajesQueYaTocaban;
  const desdesFuturos = tandasPlan.map((t) => t.desdeMs).filter((ms) => ms > ahoraMs);
  const proximaTandaMs = planAlDia && desdesFuturos.length > 0 ? Math.min(...desdesFuturos) : null;

  const { estado, inferido, error } = clasificar({
    outcome,
    cancelada: cancelacion !== null,
    hayTandaFutura: proximaTandaMs !== null,
    hayProgreso: progresos.length > 0,
    ultimaSenalMs,
    ahoraMs,
  });

  return {
    previewId: base.previewId,
    estado,
    inferido,
    nIntentos: intents.length,
    iniciadaMs,
    ultimaSenalMs,
    actor: base.actor,
    criterios: base.criterios,
    resumen: base.resumen,
    snapshotTable: base.snapshotTable,
    backendMode: base.backendMode,
    nViajesEnviados,
    progreso,
    muestrasProgreso,
    error,
    // Solo se puede afirmar con las dos cifras delante. Sin outcome no hay dedupe que comparar.
    huboDedupe:
      nViajesEnviados !== null &&
      base.resumen !== null &&
      nViajesEnviados !== base.resumen.nViajes,
    proximaTandaMs,
    cancelada:
      cancelacion === null
        ? null
        : {
            ms: cancelacion.createdAtMs,
            email: cancelacion.actor.email,
            tandasPendientes: cancelacion.cancelacion!.tandasPendientes,
            nViajesYaEnviados: cancelacion.cancelacion!.nViajesYaEnviados,
          },
  };
}

function clasificar(input: {
  outcome: EventoAudit | null;
  cancelada: boolean;
  hayTandaFutura: boolean;
  hayProgreso: boolean;
  ultimaSenalMs: number | null;
  ahoraMs: number;
}): { estado: EstadoConfirmacion; inferido: boolean; error: string | null } {
  const { outcome, cancelada, hayTandaFutura, hayProgreso, ultimaSenalMs, ahoraMs } = input;

  // BB-1676: la cancelación va PRIMERO, antes incluso del outcome, y por dos motivos distintos.
  //
  // Antes del outcome, porque el `outcome` de un job cancelado ya llega con `status: 'cancelado'` y
  // esta rama dice lo mismo un rato antes — sin ella, la fila diría "en cola" desde que se cancela
  // hasta que la última tanda se despacha, que con tandas diferidas pueden ser horas. Y sin ella, un
  // `status` que `clasificar` no conoce cae en su catch-all y la fila se pinta **Fallido en rojo**:
  // cancelar a propósito no es un fallo, y confundirlos gasta la señal roja.
  //
  // El error se sigue tomando del outcome si existe: cancelar no borra que algo hubiera fallado antes.
  if (cancelada) {
    return { estado: 'cancelada', inferido: false, error: outcome?.backendResult?.error ?? null };
  }

  if (outcome !== null) {
    const status = outcome.backendResult?.status;
    // Segunda defensa de la cancelación, independiente del evento (BB-1676). El `outcome` de un job
    // cancelado ya viene con este status, así que la fila sigue diciendo la verdad aunque el evento de
    // cancelación se haya quedado fuera de la ventana de lectura. Sin esta línea, `cancelado` no es
    // `ok` ni `partial` y cae en el catch-all de abajo: "Fallido" en rojo sobre una decisión
    // deliberada. Hallazgo del payment-safety-reviewer.
    if (status === 'cancelado') {
      return { estado: 'cancelada', inferido: false, error: outcome.backendResult?.error ?? null };
    }
    // 'ok' y 'error' son lo que escribe el código hoy; 'partial' está en el contrato para cuando
    // unos viajes den 202 y otros fallen en la misma confirmación. Un status desconocido NO se
    // trata como éxito: en una herramienta de dinero, lo que no se entiende no se cuenta como bueno.
    if (status === 'ok') return { estado: 'completado', inferido: false, error: null };
    if (status === 'partial') {
      return { estado: 'parcial', inferido: false, error: outcome.backendResult?.error ?? null };
    }
    return {
      estado: 'fallido',
      inferido: false,
      error: outcome.backendResult?.error ?? `Resultado de Backend no reconocido: ${String(status)}`,
    };
  }

  // Sin outcome. Y lo PRIMERO que se mira es si ha terminado algún chunk, no cuánto silencio hay.
  //
  // Sin ningún `progress`, NINGÚN chunk ha terminado: el dinero no se ha movido y la confirmación está
  // en cola, tenga un segundo de vida o ocho horas. El orden importaba y estaba mal: antes se
  // comprobaba el silencio primero, así que un `intent` recién escrito caía en "en curso" durante los
  // 5 minutos del margen de timeout y solo después pasaba a "en cola" — Mario veía sus dos
  // confirmaciones arrancar "en curso" sin que nada estuviera corriendo, y cambiar de estado solas.
  //
  // El margen de timeout sigue existiendo, pero solo donde tiene sentido: para distinguir un job que
  // está pagando de uno que se calló a mitad. Las dos cosas exigen que haya habido progreso.
  //
  // La deducción es segura porque el tercer caso posible ya está descartado: si el encolado falla,
  // `handleConfirm` escribe un `outcome` de error, y con outcome no se llega hasta aquí. Así que un
  // `intent` sin `progress` ni `outcome` solo puede ser "se encoló bien y aún no ha terminado nada".
  //
  // Se pierde algo a cambio, y conviene saberlo: si los workers estuvieran muertos o la cola pausada,
  // esto también diría "en cola". Pero eso no es el caso de "reconciliar un pago", que es lo que
  // `sin-resolver` significa en esta pantalla; para eso está el aviso de "sin empezar desde hace X".
  if (!hayProgreso) {
    return { estado: 'en-cola', inferido: true, error: null };
  }

  // BB-2625: con progreso, pero con una tanda que el plan de cuota dejó para más tarde. El silencio
  // hasta entonces es lo previsto, no un job muerto: antes caía en "sin resolver" a los 5 minutos
  // (`cacf8bee…`, 2026-09-29, con su segunda tanda programada para el día siguiente).
  if (hayTandaFutura) {
    return { estado: 'en-cola', inferido: false, error: null };
  }

  if (ultimaSenalMs === null) {
    // Hay progreso pero el serverTimestamp aún no ha resuelto: está vivo, acaba de escribir.
    return { estado: 'en-curso', inferido: true, error: null };
  }

  const silencio = ahoraMs - ultimaSenalMs;
  if (silencio <= MARGEN_TIMEOUT_MS) {
    return { estado: 'en-curso', inferido: false, error: null };
  }

  return {
    estado: 'sin-resolver',
    inferido: false,
    error: null,
  };
}

/**
 * El latido de la cola (`estado_cola/caes-confirm-chunks`, BB-2623), ya leído: cuándo terminó la
 * última tanda de CUALQUIER confirmación y de cuál.
 */
export interface LatidoCola {
  ultimaTandaMs: number;
  previewIdUltimaTanda: string;
  /** Confirmaciones con tandas pendientes según el worker (`confirmacionesActivas`). */
  activas: readonly string[];
}

/**
 * El documento `estado_cola/caes-confirm-chunks` validado con el esquema del contrato. `null` si no
 * valida o la fecha es ilegible: nunca se inventa un latido, y sin latido la pantalla es la de antes.
 */
export function latidoDeDocumento(data: unknown): LatidoCola | null {
  const parsed = EstadoColaSchema.safeParse(data);
  if (!parsed.success) return null;
  const ultimaTandaMs = Date.parse(parsed.data.ultimaTandaEn);
  if (Number.isNaN(ultimaTandaMs)) return null;
  return {
    ultimaTandaMs,
    previewIdUltimaTanda: parsed.data.previewIdUltimaTanda,
    activas: parsed.data.confirmacionesActivas.map((a) => a.previewId),
  };
}

/**
 * Sin ninguna tanda terminada en la cola entera durante esto, "la cola no avanza". Una tanda de 200
 * viajes tarda de ~18 s a 5 min (timeout del worker), y un reintento espera al menos 60 s
 * (`minBackoff`), así que 10 minutos no se alcanzan con la cola trabajando.
 */
export const MARGEN_COLA_MS = 10 * 60 * 1000;

/**
 * Con la cola viva, cuánto puede pasar una confirmación sin tanda propia antes de ser alarma. El latido
 * dice si avanza la COLA, no cada confirmación: con dos en vuelo, una cuyas tandas fallan y se
 * reintentan (hasta 34 h) seguiría listada mientras la otra hace latir la cola. Generoso porque el
 * 2026-10-01 Extremadura esperó hasta 13 minutos entre tandas sin que nada fallara. Decidido con Rubén
 * el 2026-10-01 (BB-2625): tope en el front, sin cambio de contrato.
 */
export const TOPE_SILENCIO_CONFIRMACION_MS = 60 * 60 * 1000;

/** El umbral a partir del cual el ETA sospecha (`eta.ts`): aquí decide si es esperar turno. */
const SILENCIO_PARA_SOSPECHAR_MS = 120 * 1000;

/**
 * Reinterpreta el estado de una confirmación SIN outcome con el latido de la cola (BB-2625). Pura y
 * aparte de `derivarEstado` porque el latido es otra colección y se lee aparte: sin latido (backend
 * anterior a BB-2623, documento ausente o ilegible) devuelve la confirmación tal cual, o sea el
 * comportamiento de antes.
 *
 * Solo toca `en-cola`, `en-curso` y `sin-resolver`, y nunca con una tanda futura en el plan: entonces
 * el silencio es lo previsto, también el de la cola entera.
 */
export function aplicarLatido(c: Confirmacion, latido: LatidoCola | null, ahoraMs: number): Confirmacion {
  if (latido === null || c.proximaTandaMs !== null) return c;
  if (c.estado !== 'en-cola' && c.estado !== 'en-curso' && c.estado !== 'sin-resolver') return c;

  // Desde cuándo se puede esperar actividad en la cola: si esta confirmación se encoló después de la
  // última tanda, el reloj empieza al encolarla, no en la tanda de ayer.
  const ultimaActividadMs = Math.max(latido.ultimaTandaMs, c.iniciadaMs ?? 0, c.ultimaSenalMs ?? 0);
  if (ahoraMs - ultimaActividadMs > MARGEN_COLA_MS) {
    return { ...c, estado: 'cola-parada', inferido: false };
  }

  const colaViva = ahoraMs - latido.ultimaTandaMs <= MARGEN_COLA_MS;
  const silencioPropioMs = ahoraMs - (c.ultimaSenalMs ?? c.iniciadaMs ?? ahoraMs);
  // La cola latió por otra confirmación DESPUÉS de la última señal de esta.
  const laColaSirveAOtra =
    colaViva &&
    latido.previewIdUltimaTanda !== c.previewId &&
    latido.ultimaTandaMs > (c.ultimaSenalMs ?? 0);
  // "Esperando turno" exige que le QUEDEN tandas. Los dos caminos de abajo parten de `en-curso` o
  // `sin-resolver`, que exigen progreso: su worker ya escribió en el latido, así que si no está en
  // `activas` no tiene nada pendiente. No espera turno: es un intent sin outcome (el `outcome` que
  // falló al escribirse) y tiene que seguir en rojo. Hallazgo del payment-safety-reviewer.
  const lequedanTandas = latido.activas.includes(c.previewId);

  if (
    c.estado === 'en-curso' &&
    silencioPropioMs > SILENCIO_PARA_SOSPECHAR_MS &&
    laColaSirveAOtra &&
    lequedanTandas
  ) {
    return { ...c, estado: 'esperando-turno', inferido: false };
  }

  if (c.estado === 'sin-resolver' && colaViva && lequedanTandas) {
    if (silencioPropioMs <= TOPE_SILENCIO_CONFIRMACION_MS) {
      return { ...c, estado: 'esperando-turno', inferido: false };
    }
    return {
      ...c,
      error:
        c.error ??
        'Esta confirmacion no avanza aunque la cola si: lleva mas de una hora sin terminar una tanda mientras se pagan otras.',
    };
  }

  return c;
}

function maximo(valores: readonly (number | null)[]): number | null {
  const presentes = valores.filter((v): v is number => v !== null);
  return presentes.length === 0 ? null : Math.max(...presentes);
}

/** Agrupa eventos por `previewId` y deriva un estado por confirmación, la más reciente primero. */
export function agruparConfirmaciones(
  eventos: readonly EventoAudit[],
  ahoraMs: number,
): Confirmacion[] {
  const porPreview = new Map<string, EventoAudit[]>();
  for (const evento of eventos) {
    const grupo = porPreview.get(evento.previewId);
    if (grupo) grupo.push(evento);
    else porPreview.set(evento.previewId, [evento]);
  }

  return [...porPreview.values()]
    .map((grupo) => derivarEstado(grupo, ahoraMs))
    .filter((c): c is Confirmacion => c !== null)
    .sort((a, b) => (b.iniciadaMs ?? 0) - (a.iniciadaMs ?? 0));
}

/**
 * Normaliza un documento de Firestore. Defensivo a propósito: esta colección la escribe el otro
 * lado del reparto, y un campo que cambie de nombre no debe tumbar la pantalla entera — el audit log
 * es justo lo que quieres poder leer el día que algo ha ido mal.
 */
export function aEventoAudit(id: string, data: Record<string, unknown>): EventoAudit | null {
  const tipo = data.tipo;
  if (tipo !== 'intent' && tipo !== 'progress' && tipo !== 'outcome' && tipo !== 'cancelacion') {
    return null;
  }

  const previewId = typeof data.previewId === 'string' ? data.previewId : null;
  if (previewId === null) return null;

  const actor = data.actor as { uid?: unknown; email?: unknown } | undefined;
  const backendResult = data.backendResult as EventoAudit['backendResult'];

  return {
    id,
    tipo,
    previewId,
    createdAtMs: aMillis(data.createdAt),
    actor: {
      uid: typeof actor?.uid === 'string' ? actor.uid : '',
      email: typeof actor?.email === 'string' ? actor.email : '(sin email)',
    },
    criterios: (data.criterios as Criterios | undefined) ?? null,
    resumen: (data.resumen as Resumen | undefined) ?? null,
    snapshotTable: typeof data.snapshotTable === 'string' ? data.snapshotTable : null,
    // Si el modo no viene, se asume 'real'. Fallar hacia "esto movió dinero" es lo prudente:
    // etiquetar de mock una confirmación real la haría parecer un ensayo.
    backendMode: data.backendMode === 'mock' ? 'mock' : 'real',
    nViajesEnviados: typeof data.nViajesEnviados === 'number' ? data.nViajesEnviados : null,
    backendResult: backendResult ?? null,
    progreso: aProgreso(data),
    cancelacion: aCancelacion(tipo, data),
    plan: aPlan(tipo, data),
  };
}

/**
 * `plan` del intent (BB-1667 Fase 2): `[{ desde: ISO, nViajes }]`. Una tanda ilegible se descarta; si
 * no queda ninguna, `null`. Equivocarse aquí hacia "sin plan" solo devuelve el comportamiento de
 * antes de BB-2625, nunca esconde un problema.
 */
function aPlan(tipo: TipoEvento, data: Record<string, unknown>): EventoAudit['plan'] {
  if (tipo !== 'intent' || !Array.isArray(data.plan)) return null;
  const tandas = data.plan.flatMap((t: unknown) => {
    if (typeof t !== 'object' || t === null) return [];
    const { desde, nViajes } = t as { desde?: unknown; nViajes?: unknown };
    const desdeMs = typeof desde === 'string' ? Date.parse(desde) : NaN;
    return Number.isNaN(desdeMs) ? [] : [{ desdeMs, nViajes: typeof nViajes === 'number' ? nViajes : 0 }];
  });
  return tandas.length > 0 ? tandas : null;
}

/**
 * Defensivo igual que `aProgreso`: si los campos no vienen como se espera, la cancelación se cuenta
 * con ceros en vez de tumbar la fila. Que ALGUIEN canceló es el hecho importante; las cifras son el
 * detalle, y una fila del audit log que no se puede pintar es peor que una con un cero de más.
 */
function aCancelacion(tipo: TipoEvento, data: Record<string, unknown>): EventoAudit['cancelacion'] {
  if (tipo !== 'cancelacion') return null;
  return {
    tandasPendientes: typeof data.tandasPendientes === 'number' ? data.tandasPendientes : 0,
    nViajesYaEnviados: typeof data.nViajesYaEnviados === 'number' ? data.nViajesYaEnviados : 0,
  };
}

function aProgreso(data: Record<string, unknown>): EventoAudit['progreso'] {
  const nEnviados = data.nEnviados;
  if (typeof nEnviados !== 'number') return null;
  return {
    nEnviados,
    nOk: typeof data.nOk === 'number' ? data.nOk : 0,
    nError: typeof data.nError === 'number' ? data.nError : 0,
  };
}

/** Timestamp de Firestore -> ms. `null` mientras el serverTimestamp no ha resuelto. */
function aMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const conToMillis = value as { toMillis?: () => number };
  if (typeof conToMillis.toMillis === 'function') return conToMillis.toMillis();
  if (typeof value === 'number') return value;
  return null;
}

/**
 * Describe los criterios aplicados, en el idioma de Ops.
 *
 * ⚠️ AQUÍ UN VACÍO NO ES "NINGUNO", ES "TODAS". La semántica está en el contrato (CLAUDE.md
 * "Colisiones resueltas"): sin CA seleccionada se pagan TODAS, incluidas las filas sin
 * `region_name`; sin rango de fechas no hay filtro de fecha. Si esta pantalla pintara un array
 * vacío como "ninguna comunidad", diría que una confirmación fue estrecha cuando en realidad fue
 * todo — y el audit log existe justo para explicar a posteriori qué se seleccionó (invariante 4).
 */
export function describirCriterios(criterios: Criterios | null): string[] {
  if (criterios === null) return ['(criterios no registrados)'];

  const cas = criterios.regionName ?? [];
  const estados = criterios.caesMitecoStatus ?? [];
  const rango = criterios.rangoFechas ?? null;
  const seleccion = criterios.seleccion ?? [];

  // La selección de la tabla se combina con AND contra los desplegables, así que ACOTA las mismas
  // dimensiones. Decir "CA: todas" cuando la selección dejó solo Asturias no es un matiz: es el
  // registro describiendo mal lo que se pagó, y este texto es la única explicación que queda de una
  // confirmación (invariante 4). Cada línea dice "todas" únicamente si NADA la restringe.
  const seleccionAcotaCa = seleccion.some((p) => p.regionName !== undefined);
  // Las DOS formas de acotar la fecha desde la tabla: una semana o un mes calendario. Contar solo
  // `semanaInicio` hacía que una confirmación acotada a abril —384.972 viajes de 5,5 M— se registrara
  // como "Fechas: sin filtro", o sea legible como el histórico entero, mientras la línea de selección
  // decía "abril de 2026" justo debajo. Dos líneas contradictorias sobre el mismo pago.
  const seleccionAcotaFechas = seleccion.some(
    (p) => p.semanaInicio !== undefined || p.mesInicio !== undefined,
  );

  const lineaCa =
    cas.length > 0
      ? `CA: ${cas.join(', ')}`
      : seleccionAcotaCa
        ? 'CA: sin filtro propio — la acota la selección de la tabla'
        : 'CA: todas (incluidas las filas sin comunidad)';

  const lineaFechas =
    rango !== null
      ? `Fechas: ${rango.desde} → ${rango.hasta}`
      : seleccionAcotaFechas
        ? 'Fechas: sin filtro propio — las acota la selección de la tabla'
        : 'Fechas: sin filtro';

  const lineas = [
    lineaCa,
    lineaFechas,
    estados.length > 0 ? `Estado MITECO: ${estados.join(', ')}` : 'Estado MITECO: todos',
  ];

  // Los criterios que llegaron después (BB-2000, BB-2004) solo se listan si acotaron algo: una
  // confirmación anterior a esos filtros no tenía esa dimensión, y escribir "todos" en ella
  // afirmaría un filtro que no existía entonces.
  if (criterios.certificationPeriod?.length) {
    lineas.push(`Periodo de certificacion MITECO: ${criterios.certificationPeriod.join(', ')}`);
  }
  if (criterios.driveFileName?.length) {
    lineas.push(`Fichero de certificacion MITECO: ${criterios.driveFileName.join(', ')}`);
  }
  if (criterios.caesSoftKillerReason?.length) {
    lineas.push(`Motivo del soft killer: ${criterios.caesSoftKillerReason.join(', ')}`);
  }
  // BB-2608, misma regla: solo si acotó. Sin esto, una confirmación que excluyó la blacklist se leería
  // en el registro igual que una que la pagó entera.
  if (criterios.isDriverBlacklisted !== undefined) {
    lineas.push(
      criterios.isDriverBlacklisted
        ? 'Conductor en blacklist: solo esos viajes'
        : 'Conductor en blacklist: excluidos',
    );
  }
  // El programa se dice con todas las letras cuando no es el de siempre: una confirmación de soft
  // killers paga viajes que Data rechazó, y eso tiene que leerse en el registro sin abrir nada más.
  if (criterios.programa === 'softKiller') {
    lineas.push('Programa: soft killers (viajes rechazados por Data, no el universo elegible)');
  }

  if (seleccion.length > 0) {
    // Los patrones se unen con OR entre ellos, y eso se dice: sin el "o", una lista de tres celdas
    // se lee como si hubieran tenido que cumplirse las tres a la vez.
    const descritos = seleccion.slice(0, MAX_PATRONES_DESCRITOS).map(describirPatron);
    const resto = seleccion.length - descritos.length;
    lineas.push(
      `Selección en la tabla (cualquiera de): ${descritos.join(' · ')}` +
        (resto > 0 ? ` y ${resto} más` : ''),
    );
  }

  return lineas;
}

/** Con más patrones que esto la línea deja de leerse; el resto se cuenta en vez de listarse. */
const MAX_PATRONES_DESCRITOS = 6;

/**
 * Normaliza una tanda de documentos ya leídos y los agrupa. Separado de la lectura para que el
 * camino completo (documento crudo -> lo que ve Ops) se pueda ejercitar sin el SDK de cliente.
 */
export function confirmacionesDeDocumentos(
  docs: readonly { id: string; data: Record<string, unknown> }[],
  ahoraMs: number,
): Confirmacion[] {
  const eventos = docs
    .map((d) => aEventoAudit(d.id, d.data))
    .filter((e): e is EventoAudit => e !== null);

  return agruparConfirmaciones(eventos, ahoraMs);
}
